import api from './client';

export interface HealthCheckup {
  id: string;
  birdId: string;
  checkupDate: string;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'RESCHEDULED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  checkupTypes: string[];
  notes?: string;
  veterinarianName?: string;
  clinicName?: string;
  scheduledBy?: string;
  completedAt?: string;
  findings?: any;
  recommendations?: string;
  followUpRequired: boolean;
  followUpDate?: string;
  cost?: number;
  documents: string[];
  bird?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHealthCheckupDto {
  birdId: string;
  checkupDate: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  checkupTypes: string[];
  notes?: string;
  veterinarianName?: string;
  clinicName?: string;
  scheduledBy?: string;
  followUpRequired?: boolean;
  followUpDate?: string;
  cost?: number;
  documents?: string[];
}

export interface UpdateHealthCheckupDto {
  checkupDate?: string;
  status?: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'RESCHEDULED';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  checkupTypes?: string[];
  notes?: string;
  veterinarianName?: string;
  clinicName?: string;
  completedAt?: string;
  findings?: any;
  recommendations?: string;
  followUpRequired?: boolean;
  followUpDate?: string;
  cost?: number;
  documents?: string[];
}

export const healthCheckupsApi = {
  // Create a new health checkup
  async create(createDto: CreateHealthCheckupDto): Promise<HealthCheckup> {
    const response = await api.post('health-checkups', createDto);
    return response.data as HealthCheckup;
  },

  // Get all health checkups
  async getAll(): Promise<HealthCheckup[]> {
    const response = await api.get('health-checkups');
    return response.data as HealthCheckup[];
  },

  // Get health checkup by ID
  async getById(id: string): Promise<HealthCheckup> {
    const response = await api.get(`health-checkups/${id}`);
    return response.data as HealthCheckup;
  },

  // Get health checkups for a specific bird
  async getByBirdId(birdId: string): Promise<HealthCheckup[]> {
    const response = await api.get(`health-checkups/bird/${birdId}`);
    return response.data as HealthCheckup[];
  },

  // Get health checkups by status
  async getByStatus(status: string): Promise<HealthCheckup[]> {
    const response = await api.get(`health-checkups/status/${status}`);
    return response.data as HealthCheckup[];
  },

  // Get scheduled health checkups
  async getScheduled(): Promise<HealthCheckup[]> {
    const response = await api.get('health-checkups/scheduled');
    return response.data as HealthCheckup[];
  },

  // Get overdue health checkups
  async getOverdue(): Promise<HealthCheckup[]> {
    const response = await api.get('health-checkups/overdue');
    return response.data as HealthCheckup[];
  },

  // Get upcoming health checkups
  async getUpcoming(days?: number): Promise<HealthCheckup[]> {
    const params = days ? `?days=${days}` : '';
    const response = await api.get(`health-checkups/upcoming${params}`);
    return response.data as HealthCheckup[];
  },

  // Get health checkups by date range
  async getByDateRange(startDate: string, endDate: string): Promise<HealthCheckup[]> {
    const response = await api.get(`health-checkups/date-range?startDate=${startDate}&endDate=${endDate}`);
    return response.data as HealthCheckup[];
  },

  // Update health checkup
  async update(id: string, updateDto: UpdateHealthCheckupDto): Promise<HealthCheckup> {
    const response = await api.patch(`health-checkups/${id}`, updateDto);
    return response.data as HealthCheckup;
  },

  // Mark checkup as completed
  async markAsCompleted(id: string, findings?: any, recommendations?: string): Promise<HealthCheckup> {
    const response = await api.patch(`health-checkups/${id}/complete`, {
      findings,
      recommendations,
    });
    return response.data as HealthCheckup;
  },

  // Reschedule checkup
  async reschedule(id: string, newDate: string): Promise<HealthCheckup> {
    const response = await api.patch(`health-checkups/${id}/reschedule`, {
      newDate,
    });
    return response.data as HealthCheckup;
  },

  // Cancel checkup
  async cancel(id: string, reason?: string): Promise<HealthCheckup> {
    const response = await api.patch(`health-checkups/${id}/cancel`, {
      reason,
    });
    return response.data as HealthCheckup;
  },

  // Delete health checkup
  async delete(id: string): Promise<void> {
    await api.delete(`health-checkups/${id}`);
  },

  // Get statistics
  async getStatistics(): Promise<any> {
    const response = await api.get('health-checkups/statistics');
    return response.data as any;
  },

  // Get checkups by priority
  async getByPriority(): Promise<Record<string, number>> {
    const response = await api.get('health-checkups/statistics/by-priority');
    return response.data as Record<string, number>;
  },

  // Get checkups by type
  async getByType(): Promise<Record<string, number>> {
    const response = await api.get('health-checkups/statistics/by-type');
    return response.data as Record<string, number>;
  },
}; 